// settleInTurn — run one RTK Query lazy trigger over many arguments safely.
//
// A `useLazyQuery` trigger keeps a subscription only for its MOST RECENT call:
// calling it again unsubscribes the previous one. With an API whose
// `keepUnusedDataFor` is 0 (liftAPI, debounceOverridesAPI,
// channelModifierAPI), that drops the earlier call's cache entry before its
// response lands, and its `unwrap()` — which reads the result back out of the
// cache — resolves `undefined`. Firing one trigger over N arguments with
// `Promise.all` therefore returned data for the last argument only; resuming
// an org with 15 banks hydrated the lifts of one of them.
//
// Awaiting each call before starting the next keeps every call subscribed
// until its result has been read. Different triggers do not share a
// subscription, so separate `settleInTurn` runs may still go in parallel.

/**
 * Call `fetchOne` for each item, one at a time, and report every outcome in
 * input order with the same shape `Promise.allSettled` gives.
 */
export default async function settleInTurn<T, R>(
  items: readonly T[],
  fetchOne: (item: T) => Promise<R>,
): Promise<PromiseSettledResult<R>[]> {
  return items.reduce<Promise<PromiseSettledResult<R>[]>>(
    (previous, item) =>
      previous.then(async (settled) => {
        try {
          return [...settled, { status: "fulfilled", value: await fetchOne(item) }];
        } catch (reason) {
          return [...settled, { status: "rejected", reason }];
        }
      }),
    Promise.resolve([]),
  );
}
